import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../store/session";
import { useI18n } from "../i18n";
import {
  getMeSettings,
  getDobStatus,
  requestEmailDeliveryVerify,
  setDateOfBirth,
  setEmailDelivery,
  type DobStatus,
  type EmailDeliveryState,
} from "../api/meSettings";
import { setStoredToken } from "../api/http";
import { publishE2EEIdentityKey } from "../api/e2eeKeys";
import { ensureWebE2EEIdentityKey, forgetWebE2EEIdentityKey, readWebE2EEIdentityKey } from "../crypto/e2eeIdentityKeyStoreWeb";
import { listMySupportTickets } from "../api/supportTickets";

export default function SettingsPage() {
  const { user, logout } = useSession();
  const { t, locale, setLocale, locales } = useI18n();

  const [emailDelivery, setEmailDeliveryState] = useState<EmailDeliveryState | null>(null);
  const [loadingDelivery, setLoadingDelivery] = useState(true);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [deliveryMsg, setDeliveryMsg] = useState<string | null>(null);
  const [deliveryErr, setDeliveryErr] = useState<string | null>(null);

  const [supportNeedsReplyCount, setSupportNeedsReplyCount] = useState(0);
  const [supportTicketCount, setSupportTicketCount] = useState(0);

  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifying, setVerifying] = useState(false);

  const [dobStatus, setDobStatus] = useState<DobStatus | null>(null);
  const [dobValue, setDobValue] = useState("");
  const [savingDob, setSavingDob] = useState(false);

  const [webE2eeIdentityKeyId, setWebE2eeIdentityKeyId] = useState(() => readWebE2EEIdentityKey()?.keyId || "");
  const [webE2eeBusy, setWebE2eeBusy] = useState(false);

  async function generateAndPublishWebE2EEIdentity() {
    setWebE2eeBusy(true);
    setDeliveryErr(null);
    setDeliveryMsg(null);

    try {
      const key = await ensureWebE2EEIdentityKey();
      await publishE2EEIdentityKey({
        keyId: key.keyId,
        algorithm: key.algorithm,
        publicKeyBase64: key.publicKeyBase64,
      });
      setWebE2eeIdentityKeyId(key.keyId);
      setDeliveryMsg("Web E2EE public identity key published. Private key remains in this browser.");
    } catch (e: any) {
      setDeliveryErr(e?.message || "Could not publish web E2EE identity key.");
    } finally {
      setWebE2eeBusy(false);
    }
  }

  function resetWebE2EEIdentity() {
    forgetWebE2EEIdentityKey();
    setWebE2eeIdentityKeyId("");
    setDeliveryMsg("Web E2EE identity key removed from this browser.");
  }

  const [messageButtonMode, setMessageButtonMode] = useState<"icon_label" | "icon" | "label">(() => {
    try {
      const saved = window.localStorage.getItem("intimsync.messages.action_button_mode");
      return saved === "icon" || saved === "label" || saved === "icon_label" ? saved : "icon_label";
    } catch {
      return "icon_label";
    }
  });

  function saveMessageButtonMode(mode: "icon_label" | "icon" | "label") {
    setMessageButtonMode(mode);
    try {
      window.localStorage.setItem("intimsync.messages.action_button_mode", mode);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingDelivery(true);
      setDeliveryErr(null);
      try {
        const [res, dob] = await Promise.all([
          getMeSettings(),
          getDobStatus(),
        ]);
        if (!alive) return;
        setEmailDeliveryState(res.emailDelivery || res.email_delivery || null);
        setDobStatus(dob || null);
      } catch (e: any) {
        if (!alive) return;
        setDeliveryErr(e?.message || "Failed to load settings.");
      } finally {
        if (alive) setLoadingDelivery(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);


  async function refreshSupportTicketSummary() {
    try {
      const res = await listMySupportTickets();
      const items = Array.isArray((res as any)?.items) ? (res as any).items : [];
      setSupportTicketCount(items.length);
      setSupportNeedsReplyCount(
        items.filter((x: any) => {
          if ((x as any)?.user_unread === true || (x as any)?.user_unread === 1) return true;
          return String(x?.status || "") === "waiting_user";
        }).length
      );
    } catch {
      setSupportTicketCount(0);
      setSupportNeedsReplyCount(0);
    }
  }

  useEffect(() => {
    void refreshSupportTicketSummary();
  }, []);

  const emailHint = useMemo(() => {
    const anyUser: any = user as any;
    const hint = anyUser?.emailHint ?? anyUser?.emailMasked ?? null;

    let localHint: string | null = null;
    try {
      localHint = window.localStorage.getItem("is_email_hint");
    } catch {}
    return hint || localHint || "—";
  }, [user]);

  const displayName = useMemo(() => {
    const anyUser: any = user as any;
    return anyUser?.displayName ?? anyUser?.display_name ?? "—";
  }, [user]);

  async function onToggleEmailDelivery(nextEnabled: boolean) {
    if (!emailDelivery) return;
    setSavingDelivery(true);
    setDeliveryErr(null);
    setDeliveryMsg(null);
    try {
      const res = await setEmailDelivery(nextEnabled);
      setEmailDeliveryState(res.emailDelivery || res.email_delivery || null);
      setDeliveryMsg(nextEnabled ? "Email notifications enabled." : "Email notifications disabled.");
    } catch (e: any) {
      setDeliveryErr(e?.message || "Failed to update email notification setting.");
    } finally {
      setSavingDelivery(false);
    }
  }

  async function onSaveDob() {
    setSavingDob(true);
    setDeliveryErr(null);
    setDeliveryMsg(null);
    try {
      await setDateOfBirth(dobValue.trim());
      const dob = await getDobStatus();
      setDobStatus(dob || null);
      setDeliveryMsg("Date of birth saved.");
      setDobValue("");
    } catch (e: any) {
      setDeliveryErr(e?.message || "Could not save date of birth.");
    } finally {
      setSavingDob(false);
    }
  }

  async function onRequestVerify() {
    setVerifying(true);
    setDeliveryErr(null);
    setDeliveryMsg(null);
    try {
      await requestEmailDeliveryVerify(verifyEmail.trim());
      setDeliveryMsg("If the address is deliverable, a verification email has been sent.");
      setVerifyEmail("");
    } catch (e: any) {
      setDeliveryErr(e?.message || "Could not start verification. Check the email address.");
    } finally {
      setVerifying(false);
    }
  }

  async function resetDeviceSession() {
    // Device-local reset for web (token + known UI keys)
    try {
      setStoredToken(null); // clears intimsync.access_token:contentReference[oaicite:10]{index=10}
      window.localStorage.removeItem("intimsync_locale");
      window.localStorage.removeItem("is_email_hint");
      window.localStorage.removeItem("is_last_email");
      window.localStorage.removeItem("calendar_entries_v1");
    } catch {
      // ignore
    }

    // Also call server logout + clear zustand user state:contentReference[oaicite:11]{index=11}
    try {
      await logout();
    } catch {
      // ignore
    }
  }

  const isVerified = !!emailDelivery?.verifiedAt;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">{t("settings.title")}</h1>
        <p className="text-sm text-slate-400">{t("settings.languageDescription")}</p>
      </header>

      {!!deliveryMsg && (
        <div className="text-sm text-emerald-300 bg-emerald-950/30 border border-emerald-800 rounded-lg px-3 py-2">
          {deliveryMsg}
        </div>
      )}
      {!!deliveryErr && (
        <div className="text-sm text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">
          {deliveryErr}
        </div>
      )}


      {/* Support */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold">Support</div>
            <div className="text-xs text-slate-400 mt-1">
              Send diagnostics, view support replies, and add more information when asked.
            </div>
          </div>

          {supportNeedsReplyCount > 0 ? (
            <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black text-white">
              {supportNeedsReplyCount} new
            </span>
          ) : supportTicketCount > 0 ? (
            <span className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-300">
              {supportTicketCount} ticket{supportTicketCount === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        <Link
          to="/support"
          onClick={() => void refreshSupportTicketSummary()}
          className="inline-flex items-center justify-center rounded-xl bg-pink-500 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-400"
        >
          Open support
        </Link>
      </section>

      {/* Language */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">{t("settings.languageSection")}</div>
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value as any)}
          className="w-full px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
        >
          {locales.map((l) => (
            <option key={l.code} value={l.code}>
              {l.nativeName} ({l.name})
            </option>
          ))}
        </select>
      </section>

      {/* Account */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">{t("settings.accountSection")}</div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
            <div className="text-xs text-slate-400">{t("common.displayName")}</div>
            <div className="text-sm text-slate-100 font-semibold mt-1">{displayName}</div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
            <div className="text-xs text-slate-400">{t("settings.emailOnFile")}</div>
            <div className="text-sm text-slate-100 font-semibold mt-1">{emailHint}</div>
            <div className="text-[11px] text-slate-400 mt-1">{t("settings.emailOnFileHelp")}</div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">Date of birth</div>

        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 space-y-2">
          <div className="text-sm font-semibold text-slate-100">
            Partner linking requirement
          </div>
          <div className="text-xs text-slate-400">
            Date of birth is required before partner linking can be accepted. It is stored securely and used for age and eligibility checks.
          </div>

          <div className="text-xs text-slate-300">
            Status: {dobStatus?.dob_set ? "Saved" : "Missing"}
            {dobStatus?.age_verified_level ? ` · Verification: ${dobStatus.age_verified_level}` : ""}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={dobValue}
              onChange={(e) => setDobValue(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            <button
              type="button"
              onClick={onSaveDob}
              disabled={savingDob || !/^\d{4}-\d{2}-\d{2}$/.test(dobValue.trim())}
              className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {savingDob ? "Saving…" : "Save DOB"}
            </button>
          </div>
        </div>
      </section>

      {/* Email delivery / verification */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">{t("settings.securitySection")}</div>

        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-3">
          <div>
            <div className="text-sm font-semibold text-slate-100">Email notifications</div>
            <div className="text-xs text-slate-400">
              {loadingDelivery
                ? "Loading…"
                : emailDelivery?.enabled
                ? "Enabled"
                : "Disabled"}
              {emailDelivery && (
                <>
                  {" · "}
                  {isVerified ? "Delivery verified" : "Not verified"}
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            disabled={loadingDelivery || savingDelivery || !emailDelivery}
            onClick={() => onToggleEmailDelivery(!(emailDelivery?.enabled ?? false))}
            className="px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm hover:border-slate-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {emailDelivery?.enabled ? "Disable" : "Enable"}
          </button>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 space-y-2">
          <div className="text-sm font-semibold text-slate-100">Verify delivery email</div>
          <div className="text-xs text-slate-400">
            Enter an address to verify delivery (safe flow; server may not reveal whether it exists).
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={verifyEmail}
              onChange={(e) => setVerifyEmail(e.target.value)}
              placeholder="name@example.com"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            <button
              type="button"
              onClick={onRequestVerify}
              disabled={verifying || !verifyEmail.trim()}
              className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {verifying ? "Sending…" : "Send verification"}
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-2 pt-1">
          <button
            className="px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm"
            onClick={resetDeviceSession}
          >
            Reset device session
          </button>

          <button
            className="px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm"
            onClick={() => logout()}
          >
            {t("common.logout")}
          </button>
        </div>
      </section>

      {/* Web E2EE identity */}
      <section data-marker="WEB_E2EE_IDENTITY_SETTINGS" className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">Web E2EE identity</div>
        <div className="text-xs text-slate-400">
          Publish this browser's public key so mobile can send encrypted flirts and private photo requests to web.
          The private key stays in this browser.
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
          <div className="text-xs text-slate-400">Current key</div>
          <div className="text-sm text-slate-100 font-semibold mt-1 break-all">
            {webE2eeIdentityKeyId || "Not generated"}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={generateAndPublishWebE2EEIdentity}
            disabled={webE2eeBusy}
            className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {webE2eeBusy ? "Publishing…" : webE2eeIdentityKeyId ? "Republish public key" : "Generate + publish"}
          </button>

          <button
            type="button"
            onClick={resetWebE2EEIdentity}
            disabled={webE2eeBusy || !webE2eeIdentityKeyId}
            className="px-4 py-2 rounded-xl border border-slate-700 text-slate-200 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            Forget web key
          </button>
        </div>
      </section>

      {/* Message display */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">Message display</div>
        <div className="text-xs text-slate-400">
          Choose how action buttons are shown on the messages screen. This is saved on this device.
        </div>

        <div className="grid sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => saveMessageButtonMode("icon_label")}
            className={
              messageButtonMode === "icon_label"
                ? "px-3 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold"
                : "px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm"
            }
          >
            🔘 Icons + labels
          </button>

          <button
            type="button"
            onClick={() => saveMessageButtonMode("icon")}
            className={
              messageButtonMode === "icon"
                ? "px-3 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold"
                : "px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm"
            }
          >
            🔘 Icons only
          </button>

          <button
            type="button"
            onClick={() => saveMessageButtonMode("label")}
            className={
              messageButtonMode === "label"
                ? "px-3 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold"
                : "px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm"
            }
          >
            Labels only
          </button>
        </div>
      </section>

      {/* More (demoted sections) */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="text-sm font-semibold">More</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <Link
            to="/places"
            className="px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm hover:border-slate-500"
          >
            {t("nav.places")}
          </Link>
          <Link
            to="/consents"
            className="px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm hover:border-slate-500"
          >
            {t("nav.consents")}
          </Link>
        </div>
      </section>
    </div>
  );
}
